import { useEffect, useState } from "react";
import { Heart, Share2, Bookmark, ArrowLeft, Calendar, User, Eye, MessageCircle } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { getPublicPublication } from "../../API/getters";

export default function PostPage() {
    const { id } = useParams();
    const [publication, setPublication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLiked, setIsLiked] = useState(false);
    const [likes, setLikes] = useState(0);

    const formatDate = (dateString) => {
        if (!dateString) return "Data desconhecida";
        const data = new Date(dateString);
        return data.toLocaleString("pt-PT", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    }

    useEffect(() => {
        const fetchPublication = async () => {
            try {
                setLoading(true);
                const data = await getPublicPublication(id);
                setPublication(data);
                setLikes(data.likes_count || 0);
                setError(null);
            } catch (err) {
                console.error("Erro ao carregar publicação:", err);
                setError("Não foi possível carregar a publicação.");
            } finally {
                setLoading(false);
            }
        };
        fetchPublication();
    }, [id]);

    
    if (loading) {
        return (
            <div className="w-full py-6">
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
                    <span className="text-gray-400 text-sm">Carregando publicação...</span>
                </div>
            </div>
        );
    }

    if (error || !publication) {
        return (
            <div className="w-full py-6">
                <div className="bg-gray-50/50 rounded-xl p-12 text-center border border-gray-100">
                    <p className="text-gray-600 mb-4">{error || 'Publicação não encontrada.'}</p>
                    <Link to="/feed" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                        Voltar para o Feed
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full py-6">
            {/* Botão Voltar */}
            <div className="mb-6">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Voltar para o Feed
                </Link>
            </div>

            {/* Card Principal */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Header com Autor */}
                <div className="p-6 md:p-8 border-b border-gray-100">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            <User className="w-6 h-6 text-emerald-500" />
                        </div>
                        <div className="flex-1">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <h3 className="font-medium text-gray-800">
                                        {publication.user_name || "Autor desconhecido"}
                                    </h3>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            <span>{formatDate(publication.created_at)}</span>
                                        </div>
                                        {publication.course_name && (
                                            <div className="flex items-center gap-1">
                                                <Eye className="w-3 h-3" />
                                                <span>{publication.course_name}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Conteúdo */}
                <div className="p-6 md:p-8 space-y-6">
                    {/* Título */}
                    {publication.title && (
                        <h1 className="text-2xl md:text-3xl font-light text-gray-800 tracking-tight">
                            {publication.title}
                        </h1>
                    )}

                    {/* Texto */}
                    <div className="prose max-w-none">
                        <p className="text-gray-600 text-base leading-relaxed whitespace-pre-line">
                            {publication.description}
                        </p>
                    </div>

                    {/* Imagem */}
                    {publication.image && (
                        <div className="rounded-xl overflow-hidden bg-gray-50">
                            <img
                                src={publication.image}
                                alt={publication.title || "Publicação"}
                                className="w-full h-auto max-h-96 object-contain"
                            />
                        </div>
                    )}
                </div>

                
            </div>
        </div>
    );
}